import URI from 'urijs'
import Vue from 'vue'

import { config } from '@/plugins/env.config'

async function retryRequest(error, attempt, callback, ...callbackArgs) {
  const maxAttempts = config.getTokenMaxAttempts
  const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

  const response = error.response ? error.response : error
  const status = response.status ? response.status : error.status

  if (status === 500 && attempt < maxAttempts) {
    await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
    return await callback(...callbackArgs, attempt + 1)
  }

  throw error
}

export async function getAuthorizationDetailForm(sponsorSlug, formSlug, attempt = 1) {
  const url = new URI(config.apiBaseUrl)
    .segment('arc')
    .segment('v1')
    .segment('sponsors')
    .segment(sponsorSlug)
    .segment('authorization-forms')

  if (formSlug) {
    url.addSearch('slug', formSlug)
  }

  url.addSearch('detail_form', true)

  try {
    return await Vue.prototype.$http.get(url.toString())
  } catch (error) {
    return await retryRequest(error, attempt, getAuthorizationDetailForm, sponsorSlug, formSlug)
  }
}

export async function getAuthorizationForm (sponsorSlug, formSlug, attempt = 1) {
  const url = new URI(config.apiBaseUrl)
    .segment('arc')
    .segment('v1')
    .segment('sponsors')
    .segment(sponsorSlug)
    .segment('authorization-forms')

  if(formSlug){
    url.addSearch('slug', formSlug)
  }

  url.addSearch('detail_form', true)

  try {
    return await Vue.prototype.$http.get(url.toString())
  }catch(error){
    return await retryRequest(error, attempt, getAuthorizationForm, sponsorSlug, formSlug)
  }
}

export async function getAuthorizationsWithoutClaim(filters, attempt = 1) {
  const getAuthorization = new URI(config.apiBaseUrl)
  getAuthorization.segment('arc')
  getAuthorization.segment('v2')
  getAuthorization.segment('authorizations')
  getAuthorization.segment('without-claims')

  if (filters.codeSearchValue) {
    getAuthorization.addSearch('code', filters.codeSearchValue)
  }

  if (filters.coverageType) {
    getAuthorization.addSearch('coverage_type', filters.coverageType)
  }

  if (filters.status) {
    getAuthorization.addSearch('status', filters.status)
  }

  if (filters.createdFrom) {
    getAuthorization.addSearch('date_from', filters.createdFrom)
  }

  if (filters.createdTo) {
    getAuthorization.addSearch('date_to', filters.createdTo)
  }

  getAuthorization.addSearch('sponsor_slug', filters.sponsorSlug)
  //TODO: Uncomment provider_slug when need filter by provider
  //getAuthorization.addSearch('provider_slug', filters.providerSlug)
  getAuthorization.addSearch('page', filters.page)
  getAuthorization.addSearch('size', filters.size)
  getAuthorization.addSearch('sort', 'createdAt,desc')

  try {
    return await Vue.prototype.$http.get(getAuthorization.toString())
  } catch (error) {
    return await retryRequest(error, attempt, getAuthorizationsWithoutClaim, filters)
  }
}

export async function getAuthorizationWithoutClaimDetail({ sponsorSlug, authorizationCode, providerSlug }, attempt = 1){
  const getAuthorization = new URI(config.apiBaseUrl)
    .segment('arc')
    .segment('v2')
    .segment('authorizations')
    .segment('without-claims')
    .segment(authorizationCode)
    .addSearch('sponsor_slug', sponsorSlug)
    .addSearch('provider_slug', providerSlug)

  try {
    return await Vue.prototype.$http.get(getAuthorization.toString())
  } catch (error) {
    return await retryRequest(error, attempt, getAuthorizationWithoutClaimDetail, { sponsorSlug, authorizationCode, providerSlug })
  }
}

export async function sendReimbursementAdditionalProducts(reimbursementId, payload, attemptNumber = 1) {
  const additionalProductsUri = new URI(config.apiBaseUrl)
  additionalProductsUri.segment('arc')
  additionalProductsUri.segment('v1')
  additionalProductsUri.segment('reimbursements')
  additionalProductsUri.segment(reimbursementId)
  additionalProductsUri.segment('additional-products')

  try {
    return await Vue.prototype.$http.post(additionalProductsUri.toString(), payload)
  }catch(error){
    return await retryRequest(error, attemptNumber, sendReimbursementAdditionalProducts, reimbursementId, payload)
  }
}

export async function sendAuthorizationAdditionalProducts(authorizationCode, payload, attemptNumber = 1) {
  const additionalProductsUri = new URI(config.apiBaseUrl)
    .segment('arc')
    .segment('v2')
    .segment('authorizations')
    .segment('without-claims')
    .segment(authorizationCode)
    .segment('additional-products')

  try {
    return await Vue.prototype.$http.post(additionalProductsUri.toString(), payload)
  }catch(error){
    return await retryRequest(error, attemptNumber, sendAuthorizationAdditionalProducts, authorizationCode, payload)
  }
}

export async function getAuthorizationSponsorStaticDocuments( sponsorSlug, coverageTypes , attempt = 1) {
  const getAuthorizationSponsorStaticDocuments = new URI(config.apiBaseUrl)
  getAuthorizationSponsorStaticDocuments.segment('arc')
  getAuthorizationSponsorStaticDocuments.segment('v2')
  getAuthorizationSponsorStaticDocuments.segment('authorizations')
  getAuthorizationSponsorStaticDocuments.segment('static')
  getAuthorizationSponsorStaticDocuments.segment('sponsor')
  getAuthorizationSponsorStaticDocuments.segment(sponsorSlug)
  getAuthorizationSponsorStaticDocuments.segment('coverage')
  getAuthorizationSponsorStaticDocuments.segment(coverageTypes)
  getAuthorizationSponsorStaticDocuments.segment('files')

  try {
    return await Vue.prototype.$http.get(getAuthorizationSponsorStaticDocuments.toString())
  } catch (error) {
    return await retryRequest(error, attempt, getAuthorizationSponsorStaticDocuments, { sponsorSlug, coverageTypes })
  }
}

export async function getAuthorizationSponsorStaticDocument( sponsorSlug, id , attempt = 1){
  const getAuthorizationSponsorStaticDocument = new URI(config.apiBaseUrl)
  getAuthorizationSponsorStaticDocument.segment('arc')
  getAuthorizationSponsorStaticDocument.segment('v2')
  getAuthorizationSponsorStaticDocument.segment('authorizations')
  getAuthorizationSponsorStaticDocument.segment('static')
  getAuthorizationSponsorStaticDocument.segment('sponsor')
  getAuthorizationSponsorStaticDocument.segment(sponsorSlug)
  getAuthorizationSponsorStaticDocument.segment('template')
  getAuthorizationSponsorStaticDocument.segment('file')
  getAuthorizationSponsorStaticDocument.segment(id)

  try {
    return await Vue.prototype.$http.get(getAuthorizationSponsorStaticDocument.toString(), {responseType: 'arraybuffer'})
  } catch (error) {
    return await retryRequest(error, attempt, getAuthorizationSponsorStaticDocuments, { sponsorSlug, id })
  }
}

export async function sendComment(authorizationCode, authorizationId, payload, attemptNumber = 1){
  const uri = new URI(config.apiBaseUrl)
    .segment('arc')
    .segment('v1')
    .segment('authorizations')
    .segment(authorizationCode)
    .segment('comment')
    .addQuery('authorization_id', authorizationId)

  try {
    return await Vue.prototype.$http.post(uri.toString(), payload)
  }catch(error){
    return await retryRequest(error, attemptNumber, sendComment, authorizationCode, authorizationId, payload)
  }
}


export async function changeStatus(sponsorSlug, authorizationCode, status, attemptNumber = 1){
  const uri = new URI(config.apiBaseUrl)
    .segment('arc')
    .segment('v2')
    .segment('authorizations')
    .segment('sponsor')
    .segment(sponsorSlug)
    .segment('code')
    .segment(authorizationCode)
    .segment('change-status')
    .addSearch('currentStatus', status.currentStatus)
    .addSearch('newStatus', status.newStatus)

  try {
    return await Vue.prototype.$http.post(uri.toString())
  }catch(error){
    return await retryRequest(error, attemptNumber, changeStatus, sponsorSlug, authorizationCode, status)
  }
}

export async function uploadAuthorizationFile({ authorizationId, formData }, attempt = 1){
  const url = new URI(config.apiBaseUrl)
    .segment('arc')
    .segment('v2')
    .segment('authorizations')
    .segment(authorizationId.toString())
    .segment('without-claims')
    .segment('files')
    .toString()

  try{
    return await Vue.prototype.$http.post(url, formData, {})
  }catch(error){
    return await retryRequest(error, attempt, uploadAuthorizationFile, { authorizationId, formData })
  }
}

export async function downloadAuthorizationFile(authorizationFileId, attempt = 1){
  const url = new URI(config.apiBaseUrl)
    .segment('arc')
    .segment('v2')
    .segment('authorizations')
    .segment('files')
    .segment(authorizationFileId.toString())
    .toString()

  try{
    return await Vue.prototype.$http.get(url, { responseType: 'arraybuffer' })
  }catch(error){
    return await retryRequest(error, attempt, downloadAuthorizationFile, authorizationFileId)
  }
}

export async function deleteAuthorizationFile(authorizationId, authorizationFileId, attempt = 1){
  const url = new URI(config.apiBaseUrl)
    .segment('arc')
    .segment('v2')
    .segment('authorizations')
    .segment(authorizationId.toString())
    .segment('without-claims')
    .segment('files')
    .segment(authorizationFileId.toString())
    .toString()

  try{
    return await Vue.prototype.$http.delete(url)
  }catch(error){
    return await retryRequest(error, attempt, deleteAuthorizationFile, authorizationId, authorizationFileId)
  }
}
